import React from "react";

// react-github-btn
// import GitHubButton from "react-github-btn";
// @mui material components
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import SoftSelect from "../../components/SoftSelect";
import { timeZoneOptions, useTimeZoneStore } from "../../stores/timeZone";

// Custom styles for the Configurator
import ConfiguratorRoot from "./ConfiguratorRoot";

import {
  setFixedNavbar,
  setMiniSidenav,
  setOpenConfigurator,
  setSidenavColor,
  setShowTimeDisplay,
  useSoftUIController,
} from "../../context";

function Configurator() {
  const [controller, dispatch] = useSoftUIController();
  const { timeZone, setTimeZone } = useTimeZoneStore();
  const { openConfigurator, miniSidenav, fixedNavbar, sidenavColor, showTimeDisplay } = controller;
  const sidenavColors = ["primary", "dark", "info", "success", "warning", "error"];

  const handleCloseConfigurator = () => setOpenConfigurator(dispatch, false);
  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleFixedNavbar = () => setFixedNavbar(dispatch, !fixedNavbar);
  const handleShowTimeDisplay = () => setShowTimeDisplay(dispatch, !showTimeDisplay);

  return (
    <ConfiguratorRoot variant="permanent" ownerState={{ openConfigurator }}>
      <SoftBox
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        pt={3}
        pb={0.8}
        px={3}
      >
        <SoftBox>
          <SoftTypography variant="h5">Panel Configuration</SoftTypography>
          <SoftTypography variant="body2" color="text">
            See our dashboard options.
          </SoftTypography>
        </SoftBox>

        <Icon
          sx={({ typography: { size, fontWeightBold }, palette: { dark } }) => ({
            fontSize: `${size.md} !important`,
            fontWeight: `${fontWeightBold} !important`,
            stroke: dark.main,
            strokeWidth: "2px",
            cursor: "pointer",
            mt: 2,
          })}
          onClick={handleCloseConfigurator}
        >
          close
        </Icon>
      </SoftBox>

      <Divider />

      <SoftBox pt={1.25} pb={3} px={3}>
        <SoftBox mb={3}>
          <SoftBox display="flex" justifyContent="space-between" alignItems="center" gap={2}>
            <SoftTypography variant="h6">Time Display</SoftTypography>
            <Switch
              checked={showTimeDisplay}
              onChange={handleShowTimeDisplay}
              inputProps={{
                "aria-label": "Time Display",
                "aria-describedby": "time-display-description",
              }}
            />
          </SoftBox>
          <SoftTypography
            component="p"
            variant="caption"
            color="text"
            id="time-display-description"
          >
            Show live UTC, CET and Gulf time at the top.
          </SoftTypography>
        </SoftBox>
        <SoftBox mb={3}>
          <SoftTypography component="label" htmlFor="merchant-timezone" variant="h6">
            Timezone
          </SoftTypography>
          <SoftTypography component="p" variant="caption" color="text" mb={1}>
            Used for dates and filters. Default: UTC.
          </SoftTypography>
          <SoftSelect
            inputId="merchant-timezone"
            value={timeZoneOptions.find((option) => option.value === timeZone)}
            options={timeZoneOptions}
            onChange={(option) => setTimeZone(option.value)}
            isSearchable
            isClearable={false}
            menuPlacement="auto"
            maxMenuHeight={240}
          />
        </SoftBox>
        <Divider />
        <SoftBox>
          <SoftTypography variant="h6">Sidenav Colors</SoftTypography>

          <SoftBox mb={0.5}>
            {sidenavColors.map((color) => (
              <IconButton
                key={color}
                sx={({ borders: { borderWidth }, palette: { white, dark }, transitions }) => ({
                  width: "24px",
                  height: "24px",
                  padding: 0,
                  border: `${borderWidth[1]} solid ${white.main}`,
                  borderColor: sidenavColor === color && dark.main,
                  transition: transitions.create("border-color", {
                    easing: transitions.easing.sharp,
                    duration: transitions.duration.shorter,
                  }),
                  backgroundImage: ({ functions: { linearGradient }, palette: { gradients } }) =>
                    linearGradient(gradients[color].main, gradients[color].state),

                  "&:not(:last-child)": {
                    mr: 1,
                  },

                  "&:hover, &:focus, &:active": {
                    borderColor: dark.main,
                  },
                })}
                onClick={() => setSidenavColor(dispatch, color)}
              />
            ))}
          </SoftBox>
        </SoftBox>

        <SoftBox mt={3} mb={2} lineHeight={1}>
          <SoftTypography variant="h6">Navbar Fixed</SoftTypography>

          <Switch checked={fixedNavbar} onChange={handleFixedNavbar} />
        </SoftBox>

        <Divider />

        <SoftBox mt={2} mb={3} lineHeight={1}>
          <SoftTypography variant="h6">Sidenav Mini</SoftTypography>

          <Switch checked={miniSidenav} onChange={handleMiniSidenav} />
        </SoftBox>
      </SoftBox>
    </ConfiguratorRoot>
  );
}

export default Configurator;
